import {
  BASE_MODAL_Z_INDEX,
  type CloseGuard,
  MODAL_Z_INDEX_STEP,
  type ModalChangeListener,
  type ModalEntry,
  type ModalOptions,
  type ModalType,
} from './types';

// ─── Option Resolution ────────────────────────────────────────────────────────

/**
 * Default closeOnEscape per modal type.
 * Popovers are non-blocking, so Esc should not try to close via ModalManager
 * (the popover component handles it natively).
 */
const ESC_DEFAULTS: Record<ModalType, boolean> = {
  dialog: true,
  drawer: true,
  confirm: true,
  sheet: true,
  popover: false,
};

function resolveEntry(id: string, options: ModalOptions): ModalEntry {
  const type = options.type ?? 'dialog';
  return {
    id,
    type,
    closeGuard: options.closeGuard ?? null,
    onClose: options.onClose,
    closeOnEscape: options.closeOnEscape ?? ESC_DEFAULTS[type],
    closeOnOverlayClick: options.closeOnOverlayClick ?? false,
    title: options.title,
    openedAt: Date.now(),
  };
}

// ─── ModalRegistry ────────────────────────────────────────────────────────────

/**
 * Framework-agnostic modal stack manager.
 *
 * Responsibilities:
 *  - Maintain a LIFO stack of open modals.
 *  - Assign incrementing z-indices based on stack position.
 *  - Run close guards before allowing user-initiated close.
 *  - Call `onClose` callbacks after confirmed close.
 *  - Provide a `forceClose` path that bypasses guards (for programmatic close).
 *  - Notify React subscribers on every state change.
 *
 * React integration is in ModalManager.tsx and use-modal.ts.
 * This class has zero React imports and is testable in isolation.
 *
 * Stack mental model:
 *   [invoice-dialog] → [address-picker-dialog] → [confirm-dirty-dialog]
 *   Topmost modal (rightmost) receives Esc, has highest z-index.
 *   Closing confirm restores focus to address-picker, etc.
 */
export class ModalRegistry {
  /** Ordered array — index 0 is oldest, last index is topmost. */
  private readonly _stack = new Array<ModalEntry>();
  private readonly _listeners = new Set<ModalChangeListener>();

  // ─── Opening ───────────────────────────────────────────────────────────────

  /**
   * Add a modal to the top of the stack (or bring an existing one to the top).
   *
   * If a modal with the same `id` is already open, it is moved to the top of
   * the stack without resetting its state. This handles the common case where
   * the same dialog is triggered twice.
   */
  open(id: string, options: ModalOptions = {}): void {
    const existing = this._stack.findIndex((m) => m.id === id);

    if (existing !== -1) {
      // Move to top — preserves closeGuard but refreshes onClose/options
      const entry = this._stack[existing];
      this._stack.splice(existing, 1);
      // Update mutable onClose in case it changed (e.g. new callback reference)
      const refreshed: ModalEntry = {
        ...entry,
        onClose: options.onClose ?? entry.onClose,
        closeOnEscape: options.closeOnEscape ?? entry.closeOnEscape,
        closeOnOverlayClick: options.closeOnOverlayClick ?? entry.closeOnOverlayClick,
      };
      this._stack.push(refreshed);
    } else {
      this._stack.push(resolveEntry(id, options));
    }

    this._notify();
  }

  // ─── Closing ───────────────────────────────────────────────────────────────

  /**
   * Attempt a user-initiated close of a specific modal.
   *
   * Flow:
   * 1. Run `closeGuard` if set. If it returns `false`, abort — returns `false`.
   * 2. Remove modal from stack.
   * 3. Call `onClose` callback.
   * 4. Notify subscribers.
   * 5. Return `true`.
   *
   * This is `async` because close guards can be async (e.g. await a secondary
   * confirm dialog). `event.preventDefault()` should be called synchronously
   * BEFORE calling this method if you're inside a keydown handler.
   */
  async close(id: string): Promise<boolean> {
    const entry = this._findById(id);
    if (!entry) return false;

    // Run close guard
    if (entry.closeGuard) {
      let allowed: boolean;
      try {
        allowed = await entry.closeGuard();
      } catch (err) {
        console.error(`[ModalRegistry] closeGuard threw for modal "${id}":`, err);
        allowed = false;
      }
      if (!allowed) return false;
    }

    this._remove(id);
    entry.onClose?.();
    return true;
  }

  /**
   * Close the topmost modal via user intent (runs guard + calls onClose).
   * Returns true if closed, false if blocked or no modal is open.
   */
  async closeTopmost(): Promise<boolean> {
    const top = this.getTopmost();
    if (!top) return false;
    return this.close(top.id);
  }

  /**
   * Immediately remove a modal from the stack WITHOUT running the close guard
   * and WITHOUT calling `onClose`.
   *
   * Use for programmatic/state-driven close:
   * - When `isOpen` prop becomes false (parent decided to close)
   * - After successful form submission
   * - Component unmount cleanup
   *
   * Idempotent — no-op if modal is not open.
   */
  forceClose(id: string): void {
    if (!this._findById(id)) return;
    this._remove(id);
  }

  /**
   * Close all open modals in LIFO order (topmost first).
   *
   * @param force  If true, bypass all close guards. Default: false.
   *
   * When `force: false`, stops at the first modal whose guard blocks close.
   */
  async closeAll(force = false): Promise<void> {
    // Snapshot the stack (reversed for LIFO order) — guards may modify stack
    const snapshot = [...this._stack].reverse();

    for (const entry of snapshot) {
      if (!this._findById(entry.id)) continue; // Already closed by a guard side-effect

      if (force) {
        this.forceClose(entry.id);
      } else {
        const closed = await this.close(entry.id);
        if (!closed) return; // Guard blocked — stop here
      }
    }
  }

  // ─── Close Guard ───────────────────────────────────────────────────────────

  /**
   * Set or clear the close guard for an open modal.
   * Useful for reactive updates when form dirty state changes.
   *
   * No-op if the modal is not currently open.
   */
  setCloseGuard(id: string, guard: CloseGuard | null): void {
    const entry = this._findById(id);
    if (entry) {
      entry.closeGuard = guard;
      // No notify — z-index and stack order unchanged
    }
  }

  // ─── Queries ───────────────────────────────────────────────────────────────

  /** Returns true if a modal with the given ID is currently open. */
  isOpen(id: string): boolean {
    return this._findById(id) !== undefined;
  }

  /** Returns true if the given modal is at the top of the stack. */
  isTopmost(id: string): boolean {
    return this.getTopmost()?.id === id;
  }

  /** Returns the topmost (most recently opened) modal, or null. */
  getTopmost(): Readonly<ModalEntry> | null {
    return this._stack[this._stack.length - 1] ?? null;
  }

  /** Returns a modal entry by ID, or undefined. */
  getById(id: string): Readonly<ModalEntry> | undefined {
    return this._findById(id);
  }

  /** Returns the current ordered stack (oldest first, topmost last). */
  getStack(): readonly ModalEntry[] {
    return this._stack;
  }

  /** Returns the number of open modals. */
  getStackDepth(): number {
    return this._stack.length;
  }

  /** Returns true if at least one modal is open. */
  hasOpenModal(): boolean {
    return this._stack.length > 0;
  }

  /**
   * Returns the computed CSS z-index for a modal based on its position in the
   * stack. The topmost modal has the highest z-index.
   *
   * Returns `BASE_MODAL_Z_INDEX` if the modal is not open.
   */
  getZIndex(id: string): number {
    const idx = this._stack.findIndex((m) => m.id === id);
    if (idx === -1) return BASE_MODAL_Z_INDEX;
    return BASE_MODAL_Z_INDEX + idx * MODAL_Z_INDEX_STEP;
  }

  // ─── Subscriptions ─────────────────────────────────────────────────────────

  /**
   * Subscribe to stack changes. Returns an unsubscribe function.
   * Used by React's `useSyncExternalStore`.
   */
  subscribe(listener: ModalChangeListener): () => void {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  // ─── Debug ─────────────────────────────────────────────────────────────────

  debug(): void {
    console.warn(
      '[ModalRegistry] stack:',
      this._stack.map((m) => ({ id: m.id, type: m.type })),
    );
    console.warn('[ModalRegistry] topmost:', this.getTopmost()?.id);
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  private _findById(id: string): ModalEntry | undefined {
    return this._stack.find((m) => m.id === id);
  }

  private _remove(id: string): void {
    const idx = this._stack.findIndex((m) => m.id === id);
    if (idx !== -1) {
      this._stack.splice(idx, 1);
      this._notify();
    }
  }

  private _notify(): void {
    for (const listener of this._listeners) {
      listener();
    }
  }
}
